/**
 * DSC Delivery Estimator (FINAL)
 * - Uses window.DSC_DELIVERY_CONFIG (theme.liquid)
 * - Business-day calculation with cutoff, non-working days, holidays
 * - Collection-based rules via Theme Editor (rules array)
 * - Writes delivery range to UI + stores in line item properties
 * - Works for BOTH:
 *    1) Native Shopify add-to-cart (form submit / product-form.js)
 *    2) DSC MVP custom add-to-cart (we'll also patch MVP file separately)
 *
 * Key idea:
 * - Main-product outputs delivery property inputs OUTSIDE the actual form.
 * - This script injects/clones those hidden inputs into every /cart/add form
 *   in the same product section, then sets values, then guards on submit.
 */

(function () {
  const cfg = window.DSC_DELIVERY_CONFIG;
  if (!cfg) return;

  /* ------------------ DOM helpers ------------------ */
  function getScope(container) {
    return (
      container.closest("[data-section-id]") ||
      container.closest("section") ||
      container.closest("product-info") ||
      document
    );
  }

  function findAddToCartForms(container) {
    const scope = getScope(container);
    const forms = Array.from(scope.querySelectorAll('form[action*="/cart/add"]'));
    if (forms.length) return forms;

    const closest = container.closest("form");
    return closest ? [closest] : [];
  }

  function safeNameSelector(name) {
    // CSS.escape not supported in some older browsers; fallback to naive selector.
    if (window.CSS && typeof window.CSS.escape === "function") {
      return `input[name="${CSS.escape(name)}"]`;
    }
    return `input[name="${name.replace(/"/g, '\\"')}"]`;
  }

  function ensureDeliveryPropInputs(container) {
    const scope = getScope(container);
    const sourceInputs = Array.from(scope.querySelectorAll("input[data-dsc-delivery-prop]"));
    if (!sourceInputs.length) return;

    const forms = findAddToCartForms(container);
    if (!forms.length) return;

    forms.forEach((form) => {
      sourceInputs.forEach((src) => {
        const name = src.getAttribute("name");
        if (!name) return;

        // If already present in the form, skip
        if (form.querySelector(safeNameSelector(name))) return;

        const clone = src.cloneNode(true);
        // Keep same name + data attribute; value set later via setHiddenProps
        clone.value = src.value || "";
        form.appendChild(clone);
      });
    });
  }

  /* ------------------ Date helpers ------------------ */
  function pad(n) { return String(n).padStart(2, "0"); }

  function toISODate(d) {
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }

  function formatDate(d) {
    return d.toLocaleDateString(undefined, { day: "2-digit", month: "long", year: "numeric" });
  }

  function parseHolidays(text) {
    const set = new Set();
    String(text || "")
      .split(/\r?\n/)
      .map((s) => s.trim())
      .filter(Boolean)
      .forEach((d) => set.add(d));
    return set;
  }

  function buildNonWorkSet(nonWorkArr) {
    const set = new Set();
    (nonWorkArr || []).forEach((n) => {
      const x = Number(n);
      if (Number.isFinite(x)) set.add(x);
    });
    return set;
  }

  const holidaysSet = parseHolidays(cfg.holidaysText);
  const nonWorkSet = buildNonWorkSet(cfg.nonWork);
  const rulesArr = Array.isArray(cfg.rules) ? cfg.rules : [];

  function isNonWorking(date) {
    if (nonWorkSet.has(date.getDay())) return true;
    if (holidaysSet.has(toISODate(date))) return true;
    return false;
  }

  function nextWorkingDay(date) {
    const d = new Date(date.getTime());
    while (isNonWorking(d)) d.setDate(d.getDate() + 1);
    return d;
  }

  function addBusinessDays(baseDate, businessDaysToAdd) {
    const d = new Date(baseDate.getTime());
    let added = 0;
    while (added < businessDaysToAdd) {
      d.setDate(d.getDate() + 1);
      if (!isNonWorking(d)) added++;
    }
    return d;
  }

  /* ------------------ Rule matching ------------------ */
  // Strategy: "Max days wins" across matched collection rules (safest).
  function getMinMaxForCollections(handlesCsv) {
    let min = Number(cfg.defaultMin || 5);
    let max = Number(cfg.defaultMax || 7);

    const handles = String(handlesCsv || "")
      .split(",")
      .map((h) => h.trim().toLowerCase())
      .filter(Boolean)
      .filter((h) => h !== "all");

    let matched = false;

    for (const h of handles) {
      const rule = rulesArr.find((r) => r && String(r.handle || "").toLowerCase() === h);
      if (!rule) continue;

      const rMin = Number(rule.min);
      const rMax = Number(rule.max);
      if (!Number.isFinite(rMin) || !Number.isFinite(rMax)) continue;

      if (!matched) {
        min = rMin;
        max = rMax;
        matched = true;
      } else {
        if (rMax > max) {
          max = rMax;
          min = Math.min(min, rMin);
        } else if (rMax === max) {
          min = Math.min(min, rMin);
        }
      }
    }

    return { min, max };
  }

  /* ------------------ Range calculation ------------------ */
  function computeRange(minDays, maxDays) {
    const now = new Date();

    const cutoffHour = Number(cfg.cutoffHour ?? 14);
    const cutoffMinute = Number(cfg.cutoffMinute ?? 0);

    const cutoffToday = new Date(now.getTime());
    cutoffToday.setHours(cutoffHour, cutoffMinute, 0, 0);

    let base = new Date(now.getTime());
    if (now.getTime() > cutoffToday.getTime()) {
      base.setDate(base.getDate() + 1);
    }

    base = nextWorkingDay(base);

    const start = addBusinessDays(base, minDays);
    const end = addBusinessDays(base, maxDays);

    return { start, end };
  }

  /* ------------------ Persist into line item properties ------------------ */
  function setHiddenProps(container, start, end) {
    ensureDeliveryPropInputs(container);

    const forms = findAddToCartForms(container);
    if (!forms.length) return;

    const startIso = toISODate(start);
    const endIso = toISODate(end);
    const rangeText = `${formatDate(start)} - ${formatDate(end)}`;

    const map = [
      { name: "properties[_Delivery start]", val: startIso },
      { name: "properties[_Delivery end]", val: endIso },
      { name: "properties[_Delivery range]", val: rangeText }
    ];

    forms.forEach((form) => {
      map.forEach(({ name, val }) => {
        const input = form.querySelector(safeNameSelector(name));
        if (input) input.value = val;
      });
    });
  }

  function attachSubmitGuard(container, minDays, maxDays) {
    ensureDeliveryPropInputs(container);

    const forms = findAddToCartForms(container);
    if (!forms.length) return;

    forms.forEach((form) => {
      // Prevent multiple bindings
      if (form.dataset.dscDeliveryGuard === "1") return;
      form.dataset.dscDeliveryGuard = "1";

      form.addEventListener(
        "submit",
        function () {
          const rangeInput = form.querySelector(safeNameSelector('properties[_Delivery range]'));
          if (rangeInput && String(rangeInput.value || "").trim()) return;

          const { start, end } = computeRange(minDays, maxDays);
          setHiddenProps(container, start, end);
        },
        { capture: true }
      );
    });
  }

  /* ------------------ UI ------------------ */
  function renderMessage(el, start, end) {
    const textEl = el.querySelector(".delivery-text");
    if (!textEl) return;

    textEl.innerHTML = `
      🚚 Expected delivery between <strong>${formatDate(start)}</strong> and <strong>${formatDate(end)}</strong>.
      <div class="delivery-note">
        <strong>Please Note:</strong> This is only estimated dates. Please refer
        <a href="https://www.jopokart.com/policies/shipping-policy" target="_blank" rel="noopener noreferrer">
          Shipping Policy
        </a>.
      </div>
    `;
  }

  function initOne(el) {
    ensureDeliveryPropInputs(el);

    const { min, max } = getMinMaxForCollections(el.dataset.collections);
    const { start, end } = computeRange(min, max);

    renderMessage(el, start, end);
    setHiddenProps(el, start, end);
    attachSubmitGuard(el, min, max);
  }

  function boot() {
    document.querySelectorAll("[data-dsc-delivery]").forEach(initOne);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
